import { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { apiClient } from '@/lib/apiClient';
import type { User } from '@supabase/supabase-js';
import { setUserContext } from '@/lib/monitoring';
import { savedHotelsStorage } from '@/utils/localStorage';
import { clearCheckoutOnLogout } from '@/utils/checkoutPersistence';
import ReactivationModal from '@/components/ReactivationModal';
import { logger } from "@/lib/logger";

type AuthContextType = {
  loading: boolean;
  user: User | null;
};

const AuthContext = createContext<AuthContextType>({ loading: true, user: null });

export const useAuth = () => useContext(AuthContext);

// Merge localStorage saves to server after login
async function mergeLocalSavesToServer(userId: string) {
  const localIds = savedHotelsStorage.getAll();
  
  if (localIds.length === 0) return;

  logger.log(`🔄 Merging ${localIds.length} local saves to server for user ${userId}`);

  try {
    // Fetch existing server saves
    const { data: existing } = await supabase
      .from('saved_properties')
      .select('hotel_id')
      .eq('user_id', userId);

    const existingIds = new Set(existing?.map(s => s.hotel_id) || []);

    // Insert only new ones
    const toInsert = localIds
      .filter(id => !existingIds.has(id))
      .map(hotel_id => ({ 
        user_id: userId, 
        hotel_id,
        property_name: `Hotel ${hotel_id}` // Placeholder name
      }));

    if (toInsert.length > 0) {
      const { error } = await supabase.from('saved_properties').insert(toInsert);
      if (error) {
        console.error('❌ Error merging local saves to server:', error);
        return;
      }
      logger.log(`✅ Merged ${toInsert.length} new saves to server`);
    }

    // Clear localStorage after successful merge
    savedHotelsStorage.clear();
  } catch (error) {
    console.error('❌ Failed to merge local saves:', error);
  }
}

// Check & cancel pending deletion, returns reactivation info if found
async function cancelPendingDeletion(
  userId: string,
  email: string
): Promise<{ firstName: string } | null> {
  try {
    const { data: pending } = await supabase
      .from('account_deletion_requests' as any)
      .select('id, scheduled_at')
      .eq('user_id', userId)
      .eq('status', 'pending')
      .maybeSingle() as any;

    if (!pending) return null;

    // Cancel the deletion
    await (supabase.from('account_deletion_requests' as any) as any)
      .update({ status: 'cancelled', cancelled_at: new Date().toISOString() })
      .eq('id', (pending as any).id);

    // Get first name for the email & modal
    const { data: profile } = await supabase
      .from('profiles')
      .select('first_name')
      .eq('id', userId)
      .single();

    const firstName = profile?.first_name || 'Member';

    // Send reactivation email
    apiClient.fireAndForget('send-deletion-scheduled-email', {
      email,
      firstName,
      scheduledDate: (pending as any).scheduled_at,
      type: 'reactivated',
    });

    // Send Telegram notification
    apiClient.fireAndForget('telegram-notify', {
      type: 'account_reactivated',
      firstName,
      email,
    });

    logger.log('✅ Account deletion cancelled — user signed back in');
    return { firstName };
  } catch (error) {
    console.error('❌ Failed to cancel pending deletion:', error);
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthContextType>({ loading: true, user: null });
  const previousUserRef = useRef<User | null>(null);

  // Reactivation modal state
  const [showReactivation, setShowReactivation] = useState(false);
  const [reactivationName, setReactivationName] = useState('');

  const handleReactivationDismiss = useCallback(() => {
    setShowReactivation(false);
  }, []);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const user = session?.user ?? null;
      const previousUser = previousUserRef.current;

      // Global PASSWORD_RECOVERY router — fires before any page-level handler
      // (e.g. Welcome.tsx) can hijack the event. Welcome.tsx still handles
      // first-time invite flows for users without a completed profile.
      if (_event === 'PASSWORD_RECOVERY') {
        try { sessionStorage.setItem('exora_recovery_session', 'true'); } catch { /* ignore */ }
        try {
          const path = window.location.pathname;
          // If we're on /welcome, let Welcome.tsx decide (invite vs reset).
          // Otherwise, force-route to the reset flow.
          if (!path.startsWith('/welcome')) {
            const onResetView =
              path === '/' && /(?:^|[?&])reset=true(?:&|$)/.test(window.location.search);
            if (!onResetView) {
              window.location.href = '/?reset=true';
              return;
            }
          }
        } catch { /* ignore */ }
      }

      setState({ loading: false, user });
      
      // Update Sentry user context for error tracking
      if (user) {
        setUserContext({
          id: user.id,
          email: user.email,
        });
        
        // User just logged in - merge localStorage to server, cancel pending deletions, init push, update shortcuts
        if (!previousUser && user) {
          // Redirect to saved return URL from quick action / protected route
          try {
            const quickReturn = sessionStorage.getItem('exora_quick_action_return');
            const authReturn = sessionStorage.getItem('exora_auth_return_to');
            const returnTo = quickReturn || authReturn;
            if (returnTo) {
              sessionStorage.removeItem('exora_quick_action_return');
              sessionStorage.removeItem('exora_auth_return_to');
              // Delay slightly so React Router is mounted
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('cap-shortcut', { detail: returnTo }));
              }, 300);
            }
          } catch { /* ignore */ }
          mergeLocalSavesToServer(user.id);
          // Only initialize push if user has completed onboarding.
          // New users still in Welcome.tsx will have push initialized on
          // home-screen landing (see Index.tsx pending-push-prompt flag).
          (async () => {
            try {
              const { data: profile } = await supabase
                .from('profiles')
                .select('terms_accepted_at')
                .eq('id', user.id)
                .maybeSingle();
              if (profile?.terms_accepted_at) {
                const m = await import('@/lib/capacitorPushNotifications');
                m.initPushNotifications(user.id).catch((err) => {
                  console.error('[Push] initPushNotifications failed in AuthProvider:', err);
                });
              } else {
                logger.log('[Push] skipping init — user still in onboarding');
              }
            } catch (err) {
              console.error('[Push] onboarding check failed:', err);
            }
          })();
          logger.log('[Shortcuts] auth login detected, refreshing logged-in shortcuts');
          import('@/lib/capacitorShortcuts').then(m => m.updateQuickActions(true)).catch(() => {});
          cancelPendingDeletion(user.id, user.email || '').then((result) => {
            if (result) {
              setReactivationName(result.firstName);
              setShowReactivation(true);
            }
          });
        }
        // Different user logged in — clean up previous user's push token
        if (previousUser && previousUser.id !== user.id) {
          import('@/lib/capacitorPushNotifications').then(m => m.removePushToken(previousUser.id)).catch((err) => {
            console.error('[Push] removePushToken failed:', err);
          });
        }
      } else {
        setUserContext(null);
        // Safety net: any path that ends in a logged-out state (token
        // expiry, multi-tab signout, scope:'others' revocation) clears
        // checkout draft + rate-lock cache so the next user on a shared
        // device never inherits the previous user's data.
        if (previousUser) clearCheckoutOnLogout();
        logger.log('[Shortcuts] auth logout detected, refreshing logged-out shortcuts');
        import('@/lib/capacitorShortcuts').then(m => m.updateQuickActions(false)).catch(() => {});
      }
      
      previousUserRef.current = user;
    });

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      const user = session?.user ?? null;
      setState({ loading: false, user });
      
      // Update Sentry user context for error tracking
      if (user) {
        setUserContext({
          id: user.id,
          email: user.email,
        });
        import('@/lib/capacitorPushNotifications').then(m => m.initPushNotifications(user.id)).catch((err) => {
          console.error('[Push] initPushNotifications failed on session restore:', err);
        });
      } else {
        setUserContext(null);
      }
      
      // Set initial quick actions based on session state
      logger.log(`[Shortcuts] initial session resolved | loggedIn=${!!user}`);
      import('@/lib/capacitorShortcuts').then(m => m.updateQuickActions(!!user)).catch(() => {});
      
      previousUserRef.current = user;
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={state}>
      {children}
      <ReactivationModal
        open={showReactivation}
        firstName={reactivationName}
        onConfirm={handleReactivationDismiss}
      />
    </AuthContext.Provider>
  );
}
